/*                                                         */
import { DESTROYED } from '#client/constants';
import { DEV } from 'esm-env';
import {
	component_context,
	dev_stack,
	is_runes,
	set_component_context,
	set_dev_stack
} from '../context.js';
import { get_boundary } from '../dom/blocks/boundary.js';
import { invoke_error_boundary } from '../error-handling.js';
import {
	active_effect,
	active_reaction,
	set_active_effect,
	set_active_reaction
} from '../runtime.js';
import { Batch, current_batch } from './batch.js';
import {
	async_derived,
	current_async_effect,
	derived,
	derived_safe_equal,
	set_from_async_derived
} from './deriveds.js';
import { aborted } from './effects.js';
import {
	hydrate_next,
	hydrate_node,
	hydrating,
	set_hydrate_node,
	set_hydrating,
	skip_nodes
} from '../dom/hydration.js';
import { create_text } from '../dom/operations.js';

/**
 *
 *
 *
 *
 */
export function flatten(sync, async, fn) {
	const d = is_runes() ? derived : derived_safe_equal;

	if (async.length === 0) {
		fn(sync.map(d));
		return;
	}

	var batch = current_batch;
	var parent = /*                 */ (active_effect);

	var restore = capture();

	var was_hydrating = hydrating;

	Promise.all(async.map((expression) => async_derived(expression)))
		.then((result) => {
			restore();

			try {
				fn([...sync.map(d), ...result]);
			} catch (error) {
				/*                                                       */
				if ((parent.f & DESTROYED) === 0) {
					invoke_error_boundary(error, parent);
				}
			}

			if (was_hydrating) {
				set_hydrating(false);
			}

			batch?.deactivate();
			unset_context();
		})
		.catch((error) => {
			invoke_error_boundary(error, parent);
		});
}

/**
 *
 *
 *
 */
export function capture() {
	var previous_effect = active_effect;
	var previous_reaction = active_reaction;
	var previous_component_context = component_context;
	var previous_batch = current_batch;

	var was_hydrating = hydrating;

	if (was_hydrating) {
		var previous_hydrate_node = hydrate_node;
	}

	if (DEV) {
		var previous_dev_stack = dev_stack;
	}

	return function restore() {
		set_active_effect(previous_effect);
		set_active_reaction(previous_reaction);
		set_component_context(previous_component_context);
		previous_batch?.activate();

		if (was_hydrating) {
			set_hydrating(true);
			set_hydrate_node(previous_hydrate_node);
		}

		if (DEV) {
			set_from_async_derived(null);
			set_dev_stack(previous_dev_stack);
		}
	};
}

/**
 *
 *
 *
 *
 *
 *
 */
export async function save(promise) {
	var restore = capture();
	var value = await promise;

	return () => {
		restore();
		return value;
	};
}

/**
 *
 *
 *
 *
 *
 */
export async function track_reactivity_loss(promise) {
	var previous_async_effect = current_async_effect;
	var value = await promise;

	return () => {
		set_from_async_derived(previous_async_effect);
		return value;
	};
}

/**
 *
 *
 *
 *
 *
 *
 *
 *
 */
export async function* for_await_track_reactivity_loss(iterable) {
	/*                                                     */
	/*                     */
	/*                                                                                                                                                                         */
	/*                 */
	/*                                                                                */

	/*                                    */
	/*         */
	const iterator = iterable[Symbol.asyncIterator]?.() ?? iterable[Symbol.iterator]?.();

	if (iterator === undefined) {
		throw new TypeError('value is not async iterable');
	}

	/*                                                                                                               */
	let normal_completion = false;
	try {
		while (true) {
			const { done, value } = (await track_reactivity_loss(iterator.next()))();
			if (done) {
				normal_completion = true;
				break;
			}
			yield value;
		}
	} finally {
		/*                                                                                                            */
		if (normal_completion && iterator.return !== undefined) {
			/*                                         */
			return /*                  */ ((await track_reactivity_loss(iterator.return()))().value);
		}
	}
}

export function unset_context() {
	set_active_effect(null);
	set_active_reaction(null);
	set_component_context(null);

	if (DEV) {
		set_from_async_derived(null);
		set_dev_stack(null);
	}
}

/**
 *
 *
 */
export async function async_body(anchor, fn) {
	var boundary = get_boundary();
	var batch = /*                */ (current_batch);
	var blocking = !boundary.is_pending();

	boundary.update_pending_count(1);
	batch.increment(blocking);

	var active = /*                 */ (active_effect);

	var was_hydrating = hydrating;
	var next_hydrate_node = undefined;

	if (was_hydrating) {
		hydrate_next();
		next_hydrate_node = skip_nodes(false);
	}

	try {
		var promise = fn(anchor);
	} finally {
		if (next_hydrate_node) {
			set_hydrate_node(next_hydrate_node);
			hydrate_next();
		}
	}

	try {
		await promise;
	} catch (error) {
		if (!aborted(active)) {
			invoke_error_boundary(error, active);
		}
	} finally {
		if (was_hydrating) {
			set_hydrating(false);
		}

		boundary.update_pending_count(-1);
		batch.decrement(blocking);

		unset_context();
	}
}
